import { faWhatsapp } from "@fortawesome/free-brands-svg-icons/faWhatsapp";
import { faCalendarXmark } from "@fortawesome/free-solid-svg-icons/faCalendarXmark";
import { faCircle } from "@fortawesome/free-solid-svg-icons/faCircle";
import { faRotateLeft } from "@fortawesome/free-solid-svg-icons/faRotateLeft";
import { add } from "date-fns";
import isEmpty from "lodash/isEmpty";
import isFunction from "lodash/isFunction";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Flex } from "../../../../components/Flex";
import { Spacing } from "../../../../theme/Spacing";
import { Text } from "../../../../components/Text";
import { AtiraIcon } from "../../../../components/AtiraIcon";
import { Info } from "../../../../components/Info";
import { Entry } from "../../../../model/entry/Entry";
import { InputTypes } from "../../../../model/form/types/InputTypes.enum";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { standardDate } from "../../../../utils/Date";
import { isBlankValue } from "../../../../utils/isBlankValue";
import { isCustomDateInput } from "../../../../utils/isCustomDateInput";
import { linkify } from "../../../../utils/String";

import { Button } from "../../../../components/Button";
import { UserRoles } from "../../../../model/user/types/UserRoles";
import { EntityAssignCheckbox } from "../../../../components/EntityAssignCheckbox";
import { useDrawersContext } from "../../../tasks/drawers-context";
import { toUSD } from "../../../../utils/pricingUtils";
import { Progress, Tooltip } from "../../../../utils/local-antd";
import { getTranslatedKanbanColumnName } from "../../../kanban/KanbanUtils";
import { entryActions } from "../../../../redux/entry/entry.slice";
import { entrySliceSelectors } from "../../../../redux/entry/entry.selector";
import { formActions } from "../../../../redux/forms/form.slice";
import capitalize from "lodash/capitalize";
import { SpinnerFullScreen } from "../../../../components/SpinnerFullScreen";
import omit from "lodash/omit";
import { KanbanCard } from "../../../../model/kanban/KanbanCard";
import { TestIDs } from "../../../../tests/types/testIds";
import { WarningModal } from "../../../../components/WarningModal";
import { EntryDetailsContactActions } from "./EntryDetailsContactActions";
import { EntryDetailsRow } from "./EntryDetailsRow";
import {
  chatLastMessageIcon,
  chatLastMessagePreview,
} from "../../../integration/utils/integration-utils";

const StyledFlex = styled(Flex)`
  flex-wrap: wrap;
  gap: ${Spacing.s};
`;

const BoldText = styled(Text)`
  font-weight: 700;
`;

const ChatLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.s};
  color: ${({ theme }) => theme.main};
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const LastMessage = styled(Text)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  unicode-bidi: plaintext;
`;

const ValueText = styled(Text)`
  word-break: break-word;
`;

type Props = {
  retrieve?: (entry: Entry | Partial<Entry>) => Promise<void>;
};

export const EntryDetailsDrawerInfoTab: React.FC<Props> = ({ retrieve }) => {
  const [retrieveLoading, setRetrieveLoading] = useState(false);
  const [cancelBookingField, setCancelBookingField] = useState<string | null>(
    null,
  );
  const [cancelBookingLoading, setCancelBookingLoading] = useState(false);

  const { t } = useTranslation();
  const { entry, setEntry, setEntryDetailsVisible } = useDrawersContext();
  const dispatch = useAppDispatch();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const tableSettings = useAppSelector(
    entrySliceSelectors.selectUserEntryTableSettings,
  );
  const entryDate = standardDate(entry?.createdAt || "", user, true);
  const canSeeAssignees = user?.roles?.includes(UserRoles.ORGANIZATION);
  const showLoading = isEmpty(omit(entry, ["_id", "kind"]));

  const getFieldInput = (field: string) =>
    entry?.parentForm?.inputs?.find((inp) => inp.name === field);

  const isBookingField = (field: string) => {
    const input = getFieldInput(field);
    return isCustomDateInput(input?.type, input?.dateInputConfig);
  };

  const getFieldValue = (field: string, value: string) => {
    if (typeof (value as unknown) === "boolean") {
      return t((value as unknown) === true ? "common.yes" : "common.no");
    }

    const input = getFieldInput(field);
    const isDateField =
      input?.type?.toLowerCase() === InputTypes.DATE.toLowerCase();

    if (!isDateField) {
      return value;
    }

    // isCustomDateInput (not `input.custom`, which only flags a field as
    // "added via the custom-fields step" - unrelated to whether it's
    // bookable) - a booking field must show its hour in *its own*
    // configured timezone, not the browser's/date-only.
    const isBooking = isCustomDateInput(input?.type, input?.dateInputConfig);

    return standardDate(
      value,
      user,
      isBooking,
      false,
      isBooking ? input?.dateInputConfig?.timezone : undefined,
    );
  };

  // Phone numbers, emails and dates read left to right in any page language
  const readsLeftToRight = (field: string) =>
    field === "phone" ||
    field === "email" ||
    getFieldInput(field)?.type?.toLowerCase() === InputTypes.DATE.toLowerCase();

  const getFieldActions = (field: string, value: string) => {
    if (field === "phone" || field === "email") {
      return <EntryDetailsContactActions kind={field} value={value} />;
    }

    if (isBookingField(field)) {
      return (
        <Tooltip title={t("entries.details.cancelBooking.tooltip")}>
          <Button
            icon={faCalendarXmark}
            backgroundColor="transparent"
            color="danger"
            width="2rem"
            height="2rem"
            margin="none"
            padding="none"
            onClick={() => setCancelBookingField(field)}
            dataTestId={TestIDs.entries.details.cancelBooking.button(field)}
          />
        </Tooltip>
      );
    }

    return null;
  };

  const onCancelBooking = async () => {
    if (!entry?._id || !cancelBookingField) {
      return;
    }

    try {
      setCancelBookingLoading(true);

      await dispatch(
        entryActions.updateEntryById({
          userId: user._id,
          entryId: entry._id,
          fields: { [cancelBookingField]: "" },
        }),
      ).unwrap();

      const refreshedEntry = await dispatch(
        entryActions.getEntryById({ entryId: entry._id, userId: user._id }),
      ).unwrap();

      setEntry(refreshedEntry);

      dispatch(formActions.getForms({ userId: user._id }));

      setCancelBookingField(null);
      AtiraToast.success(t("entries.details.cancelBooking.success"));
    } catch (e) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setCancelBookingLoading(false);
    }
  };

  const getFieldLabel = (field: string) => {
    if (entry?.parentForm?.orphan === true) {
      return t(`common.${field}`);
    }

    return (
      capitalize(
        entry?.parentForm?.inputs?.find((input) => input.name === field)?.label,
      ) || tableSettings?.columns?.find((col) => col.field === field)?.label
    );
  };

  const onRetrieve = async () => {
    try {
      if (!entry) {
        return;
      }

      setRetrieveLoading(true);

      await retrieve?.(entry);
    } catch (e) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setRetrieveLoading(false);
    }
  };

  const lastMessagePreview = entry?.whatsappChat
    ? chatLastMessagePreview(entry.whatsappChat, t)
    : "";

  // A modifier click opens a new tab and leaves this drawer where it was
  const onOpenChat = (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey) {
      return;
    }

    setEntryDetailsVisible(false);
    setEntry(null);
  };

  const getCardProgress = (card: KanbanCard) => {
    const total = card?.column?.kanban?.columnsCount || 0;
    const currentOrder = card?.column?.order ?? -1;

    if (total <= 0 || currentOrder < 0) {
      return null;
    }

    const currentIndex = Math.min(currentOrder, total - 1);
    const percent = Math.round(((currentIndex + 1) / total) * 100);

    return {
      total,
      currentIndex,
      // Default columns are: closed-won, closed-lost and closed-to-competition
      percent: card?.column?.default ? 100 : percent,
    };
  };

  if (!entry) {
    return null;
  }

  if (showLoading) {
    /* See: onViewEntity() in utils/Reports.tsx */
    return <SpinnerFullScreen />;
  }

  return (
    <Flex flexDirection="column" gap="s">
      {Boolean(entry.markedForDeletion) ? (
        <Info
          info={t("entries.delete.modal.notice.title", {
            date: standardDate(
              add(new Date(entry?.markedForDeletionAt || ""), {
                days: 30,
              }),
            ),
          })}
          color="warning"
        />
      ) : null}

      <Flex flexDirection="column">
        {entry?.kind ? (
          <EntryDetailsRow label={t("common.kind")}>
            {t(`entries.table.entry.${entry?.kind?.toLowerCase()}`)}
          </EntryDetailsRow>
        ) : null}

        {entry?.parentForm?.title ? (
          <EntryDetailsRow label={t("entries.table.header.form_title")}>
            {entry?.parentForm.orphan
              ? t("forms.manual_entry")
              : entry?.parentForm.title}
          </EntryDetailsRow>
        ) : null}
      </Flex>

      {canSeeAssignees && entry.assigneeIds?.length ? (
        <EntityAssignCheckbox value={entry.assigneeIds} disabled />
      ) : null}

      <Flex flexDirection="column" gap="s" marginTop="m">
        <BoldText>{t("common.details")}</BoldText>

        <Flex flexDirection="column">
          {Object.entries(entry?.fields || {}).map(([key, value]) =>
            !isBlankValue(value) ? (
              <EntryDetailsRow
                key={key}
                label={getFieldLabel(key) || t(`common.${key}`)}
                dir={readsLeftToRight(key) ? "ltr" : "auto"}
                actions={getFieldActions(key, value)}
                dataTestId={TestIDs.entries.details.field(key)}
              >
                {getFieldValue(key, value)}
              </EntryDetailsRow>
            ) : null,
          )}
        </Flex>
      </Flex>

      {entry.whatsappChat ? (
        <Flex flexDirection="column" gap="s" marginTop="m">
          <BoldText>{t("entries.details.whatsapp.title")}</BoldText>

          <Flex flexDirection="column">
            <EntryDetailsRow label={t("entries.details.whatsappChat")}>
              <ChatLink
                to={`/integration/whatsapp-meta?chat=${entry.whatsappChat._id}`}
                onClick={onOpenChat}
                data-testid={TestIDs.entries.details.whatsappChat}
              >
                <AtiraIcon icon={faWhatsapp} color="main" />
                <bdi>
                  {entry.whatsappChat.name ||
                    (entry.whatsappChat.waId
                      ? `+${entry.whatsappChat.waId}`
                      : entry.whatsappChat.username
                        ? `@${entry.whatsappChat.username}`
                        : t("entries.details.whatsappChat.open"))}
                </bdi>
              </ChatLink>
            </EntryDetailsRow>

            {lastMessagePreview ? (
              <EntryDetailsRow
                label={t("entries.details.whatsappChat.lastMessage")}
              >
                <Flex alignItems="center" gap="s">
                  <AtiraIcon
                    icon={chatLastMessageIcon(
                      entry.whatsappChat.lastMessageType,
                    )}
                    color="main"
                  />
                  <LastMessage
                    data-testid={
                      TestIDs.entries.details.whatsappChatLastMessage
                    }
                  >
                    {lastMessagePreview}
                  </LastMessage>
                </Flex>
              </EntryDetailsRow>
            ) : null}

            {entry.whatsappChat.lastMessageAt ? (
              <EntryDetailsRow
                label={t("entries.details.whatsappChat.lastMessageAt")}
                dir="ltr"
              >
                {standardDate<string>(
                  entry.whatsappChat.lastMessageAt,
                  user,
                  true,
                )}
              </EntryDetailsRow>
            ) : null}
          </Flex>
        </Flex>
      ) : null}

      {entry?.description ? (
        <Flex flexDirection="column" gap="s">
          <BoldText>{t("common.description")}</BoldText>

          <ValueText
            dangerouslySetInnerHTML={{
              __html: linkify(entry.description),
            }}
          />
        </Flex>
      ) : null}

      {entry.kanbanCards?.length ? (
        <Flex flexDirection="column" gap="s" marginTop="m">
          <Info info={t("common.deals")} />
          {entry.kanbanCards.map((card) => {
            const cardProgress = getCardProgress(card);
            return (
              <Flex flexDirection="column" gap="s" key={card._id}>
                <BoldText>{card.title}:</BoldText>

                <StyledFlex>
                  <Flex alignItems="center" gap="s">
                    <AtiraIcon icon={faCircle} size="xs" color="main" />
                    <BoldText>{t("common.amount")}:</BoldText>
                  </Flex>
                  <ValueText>
                    {toUSD(parseInt(card?.amount || "0") * 100)}
                  </ValueText>
                </StyledFlex>

                {card?.closingDate ? (
                  <StyledFlex>
                    <Flex alignItems="center" gap="s">
                      <AtiraIcon icon={faCircle} size="xs" color="main" />
                      <BoldText>{t("common.closingDate")}:</BoldText>
                    </Flex>
                    <ValueText>{standardDate(card?.closingDate)}</ValueText>
                  </StyledFlex>
                ) : null}

                {card?.note ? (
                  <StyledFlex>
                    <Flex alignItems="center" gap="s">
                      <AtiraIcon icon={faCircle} size="xs" color="main" />
                      <BoldText cutText>{t("common.note")}:</BoldText>
                    </Flex>
                    <ValueText>{card?.note}</ValueText>
                  </StyledFlex>
                ) : null}

                {cardProgress ? (
                  <StyledFlex>
                    <Flex alignItems="center" gap="s">
                      <AtiraIcon icon={faCircle} size="xs" color="main" />
                      <BoldText>{t("common.progress")}:</BoldText>
                    </Flex>
                    <Flex gap="s" alignItems="center">
                      <Progress
                        steps={cardProgress.total}
                        percent={cardProgress.percent}
                        size="default"
                      />
                      <ValueText>
                        {" - "}
                        {getTranslatedKanbanColumnName(
                          card.column?.name,
                          card.column?.kanban?.template,
                        )}
                      </ValueText>
                    </Flex>
                  </StyledFlex>
                ) : null}
              </Flex>
            );
          })}
        </Flex>
      ) : null}

      {Boolean(entry.markedForDeletion) && isFunction(retrieve) ? (
        <Button
          onClick={onRetrieve}
          icon={faRotateLeft}
          width="8rem"
          height="2.3rem"
          loading={retrieveLoading}
        >
          {t("common.retrieve")}
        </Button>
      ) : null}

      <Flex flexDirection="column" gap="s" marginTop="m">
        <BoldText>{t("common.info")}</BoldText>

        <Flex flexDirection="column">
          <EntryDetailsRow
            label={t("entries.table.header.submit_time")}
            dir="ltr"
          >
            {entryDate}
          </EntryDetailsRow>

          <EntryDetailsRow label={t("common.id")} dir="ltr">
            {entry?._id}
          </EntryDetailsRow>
        </Flex>
      </Flex>

      <WarningModal
        title={t("entries.details.cancelBooking.modal.title")}
        description={t("entries.details.cancelBooking.modal.description", {
          field: cancelBookingField
            ? getFieldLabel(cancelBookingField) ||
              t(`common.${cancelBookingField}`)
            : "",
        })}
        isOpen={Boolean(cancelBookingField)}
        onClose={() => setCancelBookingField(null)}
        onConfirm={onCancelBooking}
        loading={cancelBookingLoading}
        confirmButtonText={t("entries.details.cancelBooking.modal.confirm")}
        confirmButtonDataTestId={
          TestIDs.entries.details.cancelBooking.modalConfirm
        }
      />
    </Flex>
  );
};
